import { Component, Element, h, Host, Prop } from "@stencil/core";
import { BaseUserModel } from "@trumpf/ux.utils";

const baseClass = "uic-app-user-info";

/**
 * @slot info - Additional info relevant for the user.
 */
@Component({
    tag: "uic-app-user-info",
    styleUrl: "app-user-info.scss",
    shadow: false,
})
export class AppUserInfo {
    @Element() host: HTMLUicAppUserInfoElement;

    /** The user data */
    @Prop() userData: BaseUserModel | null = null;

    /** Link to the user profile page */
    @Prop() userProfileUrl?: string;

    /** Text for the user profile link */
    @Prop() userProfileText: string = "User Profile";

    /** Text that is shown if no userData is provided */
    @Prop() noDataText: string = "No user data available";

    /** This works because the component is only used with a named slot by the app-header */
    private hasNamedSlot = (): boolean => {
        return this.host.querySelector("[slot]") !== null;
    };

    private renderUserName = () => {
        const { displayName, userName } = this.userData;
        if (
            (typeof displayName !== "string" || displayName.length < 1) &&
            (typeof userName !== "string" || userName.length < 1)
        )
            return null;
        return (
            <div class={`${baseClass}__username`}>
                <strong>{displayName || userName}</strong>
            </div>
        );
    };

    private renderRole = () => {
        const { role } = this.userData;
        if (typeof role !== "string" || role === "") return null;
        return <div class={`${baseClass}__role`}>{role}</div>;
    };

    private renderEmail = () => {
        const { email } = this.userData;
        if (typeof email !== "string" || email === "") return null;
        return <div class={`${baseClass}__email`}>{email}</div>;
    };

    private renderProfileLink = () => {
        const { userProfileUrl, userProfileText } = this;
        if (typeof userProfileUrl !== "string" || userProfileUrl.length < 1)
            return null;
        return (
            <div class={`${baseClass}__links`}>
                <a
                    class="uic-textlink"
                    href={userProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {userProfileText}
                </a>
            </div>
        );
    };

    private renderNoDataNotice = () => {
        if (this.noDataText === "") return null;
        return <div class={`${baseClass}__no-data`}>{this.noDataText}</div>;
    };

    render() {
        const hasSlot = this.hasNamedSlot();
        const hasUserData =
            typeof this.userData === "object" && this.userData !== null;
        const hasNoData = !hasUserData && !hasSlot;
        const hasProfileLink =
            this.userProfileUrl && this.userProfileUrl !== "";
        return (
            <Host class={baseClass}>
                <div class={`${baseClass}__avatar`}>
                    <uic-icon name="uic:user" />
                </div>
                <div class={`${baseClass}__content`}>
                    <div class={`${baseClass}__row`}>
                        {hasNoData && this.renderNoDataNotice()}
                        {hasUserData && this.renderUserName()}
                        {hasUserData && this.renderRole()}
                        {hasUserData && this.renderEmail()}
                    </div>
                    <div
                        class={{
                            [`${baseClass}__row`]: hasSlot,
                            [`${baseClass}--hidden`]: !hasSlot,
                        }}
                    >
                        <slot />
                    </div>
                    {hasProfileLink && (
                        <div class={`${baseClass}__row`}>
                            {this.renderProfileLink()}
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
